import IconExpandMore from 'assets/icons/expand-more.svg';
import { StaticPDPProduct } from 'modules/product-details/types';
import { useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import { Gtm, gtm } from 'tracking/gtm';

interface Props {
  product: StaticPDPProduct;
  selectedVariantId: string | null;
  onVariantIdChange: (variantId: string | null) => void;
}

const VariantSelector: React.FC<Props> = ({
  product,
  selectedVariantId,
  onVariantIdChange,
}) => {
  const { variants } = product;

  const onVariantChange: React.ChangeEventHandler<HTMLSelectElement> =
    useCallback(
      (event) => {
        const variantId = event.target.value;
        if (selectedVariantId === variantId) return;
        onVariantIdChange(variantId);

        gtm({
          group: Gtm.GroupName.Product,
          name: Gtm.ProductEventName.SwitchVariant,
          data: {
            product: {
              productTitle: product.title,
              productId: product.id + '',
              productType: product.productType,
              tags: product.tags.join(','),
              variantId: variantId + '',
            },
          },
        });
      },
      [
        product.id,
        product.productType,
        product.tags,
        product.title,
        selectedVariantId,
        onVariantIdChange,
      ]
    );

  return (
    <div data-test="variant-selection" className="w-full">
      <label
        htmlFor="size"
        className="mb-1 inline-block text-sm text-text-secondary"
      >
        <FormattedMessage id="pdp:buy-panel:size" />
      </label>

      {variants.length === 1 ? (
        <div className="h-[42px] whitespace-nowrap rounded-sm border border-grey-200 bg-other-white py-2 pl-4 pr-6 focus:outline-hidden">
          {variants[0].title}
          {variants[0].availableForSale ? null : (
            <FormattedMessage id="pdp:buy-panel:out-of-stock" />
          )}
        </div>
      ) : (
        <div className="relative">
          <IconExpandMore className="pointer-events-none absolute right-3 top-1/2 h-5 w-5 -translate-y-1/2 transform text-text-secondary" />

          <select
            className="h-[42px] w-full appearance-none rounded-lg border border-grey-200 bg-other-white pl-3 pr-6 text-text-secondary focus:outline-hidden"
            id="size"
            value={selectedVariantId || ''}
            onBlur={onVariantChange}
            onChange={onVariantChange}
          >
            {variants.map((variant) => (
              <option key={variant.id} value={variant.id}>
                {variant.title}
                {variant.availableForSale ? null : (
                  <FormattedMessage id="pdp:buy-panel:out-of-stock" />
                )}
              </option>
            ))}
          </select>
        </div>
      )}
    </div>
  );
};

export default VariantSelector;
